/**
 * The always-visible offline/sync status pill (brief §2, mockup topbar).
 *
 * Presentational: it takes a `SyncStatus` value and renders it. The engine
 * subscription lives in `useSyncStatus()` (src/ui/state/useSyncStatus.ts) so
 * this component can be rendered in any state a test wants without standing up
 * IndexedDB.
 *
 * `aria-live="polite"` on the label: going offline mid-survey is the single
 * most important state change in the app, and the surveyor is looking at a
 * disconnect, not at the top of the screen.
 */

import type { ReactNode } from 'react';

import type { SyncStatus } from '../../db/sync';
import { syncPillView } from './syncPillState';

export interface SyncPillProps {
  status: SyncStatus;
  /** False when the device holds no surveyor token. Defaults to linked. */
  linked?: boolean;
}

export function SyncPill({ status, linked = true }: SyncPillProps): ReactNode {
  const view = syncPillView(status, { linked });
  const dotClass = [
    'syncpill__dot',
    view.pulse ? 'syncpill__dot--pulse' : '',
    view.tone === 'ok' ? 'syncpill__dot--ok' : '',
    view.tone === 'error' ? 'syncpill__dot--error' : '',
    view.tone === 'attention' ? 'syncpill__dot--attention' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const toneClass =
    view.tone === 'error'
      ? 'syncpill syncpill--error'
      : view.tone === 'attention'
        ? 'syncpill syncpill--attention'
        : 'syncpill';

  const inner = (
    <>
      <span className={dotClass} aria-hidden="true" />
      <span className="syncpill__label" aria-live="polite">
        {view.label}
      </span>
      <span className="sr-only">{view.detail}</span>
    </>
  );

  /*
   * A LINK when there is somewhere to go, a badge when there is not.
   *
   * "5 photos need you" used to be the whole of what a surveyor got: a count in
   * the corner, its explanation in a `title` attribute no phone shows, and a
   * tap that did nothing (Chip, 2026-08-18). In the "needs you" state the pill
   * is now the way to the list of exactly those items — the same amber, the
   * same words, and an underline so it READS as tappable, because a control that
   * looks like a badge is a badge whatever it does on tap. Every other state
   * stays a `<span>`: a link to an empty list is a control that does nothing.
   */
  if (view.href) {
    return (
      <a
        className={`${toneClass} syncpill--link`}
        data-legibility="display"
        title={view.detail}
        href={view.href}
      >
        {inner}
      </a>
    );
  }
  return (
    <span className={toneClass} data-legibility="display" title={view.detail}>
      {inner}
    </span>
  );
}
